import posthog from 'posthog-js';
import { RADAR_FEATURE_FLAG } from './EnvConfigUtils';

// Feature flags (PostHog). Keep flag keys here so gates read from one place.
export const PROJECTS_V2_FLAG = 'projects-v2';

// The projects "v2" experience: deliverables + tasks + documents, the redesigned
// project overview, the new project-form fields, and the richer projects rows.
// Everything in that set is gated behind this flag.
export const isProjectsV2Enabled = (): boolean => !!posthog.isFeatureEnabled(PROJECTS_V2_FLAG);

export const INVOICE_WORK_ORDER_CHECK_FLAG = 'invoice-work-order-check';

// The invoice → work order check and everything derived from it: the mismatch confirmation on a
// parsed upload, the comparison table kept on the invoice form, and the "Enrich Data?" asset
// suggestion banner on invoice detail. All three come from one backend payload (`work_order_check`,
// which carries `asset_suggestion` inside it), so one flag covers the set.
//
// This sits on top of the backend's own switches (`openwrench.decisions.invoice.enabled` and
// `.surface_in_response`), which are global config. This flag is what makes a staged, per-company
// rollout possible while those stay on.
//
// Gating the REQUEST matters most: with the flag off the AI forms stop sending `workOrderId` to the
// extraction endpoint, so the backend never runs the decision and no verdict exists to render. The
// banner is gated separately because `assetSuggestion` is persisted on the invoice — without that,
// invoices created while the flag was on would keep offering asset writes after it was turned off.
export const isInvoiceWorkOrderCheckEnabled = (): boolean =>
	!!posthog.isFeatureEnabled(INVOICE_WORK_ORDER_CHECK_FLAG);

// The quote counterpart: off until enabled in PostHog. Withholding `workOrderId` from the quote
// upload is enough to turn the whole check off.
export const QUOTE_WORK_ORDER_CHECK_FLAG = 'quote-work-order-check';

export const isQuoteWorkOrderCheckEnabled = (): boolean =>
	!!posthog.isFeatureEnabled(QUOTE_WORK_ORDER_CHECK_FLAG);
export const ADMIN_USER_GROUPS_FLAG = 'Admin-User-Groups';

// The admin user-groups experience: the "User Groups" tab on the buyer and supplier admin pages,
// its index page (which redirects to Users when the flag is off, so a direct link can't reach it),
// and the "User Groups" field on the invite / edit user forms. With the field hidden
// `userGroupIds` is undefined, so `syncUserGroupsForUser` makes no request and saving a user
// leaves their membership untouched.
export const isAdminUserGroupsEnabled = (): boolean =>
	!!posthog.isFeatureEnabled(ADMIN_USER_GROUPS_FLAG);

// Radar — the Agent Operating Procedures feature. The key lives in
// `EnvConfigUtils` beside `AGENT_CHAT_FEATURE_FLAG`, where the menu entry is.
//
// Gating the REQUEST is what matters, as with the invoice check above. Radar's
// two work-order surfaces — the banner on the details tab and the "Radar
// Activity" tab — each decide whether to appear by asking the agent service
// for that work order's runs, so ungated they are two calls on every
// work-order view by every buyer. The gate belongs in front of the read, not
// just the markup. Routes stay reachable by URL, as Oscar's are.
export const isRadarEnabled = (): boolean => !!posthog.isFeatureEnabled(RADAR_FEATURE_FLAG);
